/**
 * Chrome shared between routes (`/`, `/flpdiff`, viewer).
 *
 * Nav + footer live here so every page renders the same shell. The
 * waitlist form helper is exported too: it's used twice on the landing
 * (hero + final CTA) and may be embedded into other pages later.
 */

import { h } from "./ui.ts";
import { MAILCOACH_SUBSCRIBE_URL } from "./config.ts";

export const GITHUB_URL = "https://github.com/dawhubapp/flpdiff";

export type ActiveRoute = "landing" | "flpdiff" | "flpinfo" | "viewer";

interface NavLink {
  readonly href: string;
  readonly label: string;
  readonly cta?: boolean;
  readonly external?: boolean;
}

const NAV_LINKS: Record<ActiveRoute, readonly NavLink[]> = {
  landing: [
    { href: "/flpdiff", label: "FLPDIFF" },
    { href: "/flpinfo", label: "FLPINFO" },
    { href: "#faq", label: "FAQ" },
    { href: "#waitlist", label: "JOIN →", cta: true },
  ],
  flpdiff: [
    { href: "/", label: "DAWHUB" },
    { href: "/flpinfo", label: "FLPINFO" },
    { href: "/flpdiff#viewer", label: "VIEWER" },
    { href: GITHUB_URL, label: "GITHUB ↗", external: true, cta: true },
  ],
  flpinfo: [
    { href: "/", label: "DAWHUB" },
    { href: "/flpdiff", label: "FLPDIFF" },
    { href: "#inspector", label: "INSPECT" },
    { href: GITHUB_URL, label: "GITHUB ↗", external: true },
  ],
  viewer: [
    { href: "/", label: "DAWHUB" },
    { href: "/flpdiff", label: "FLPDIFF" },
    { href: "/flpinfo", label: "FLPINFO" },
    { href: GITHUB_URL, label: "GITHUB ↗", external: true },
  ],
};

const NAV_TAGLINE: Record<ActiveRoute, string> = {
  landing: "version control for .flp files",
  flpdiff: "open-source diff for FL Studio projects",
  flpinfo: "single-file project inspector",
  viewer: "drop two .flp files",
};

export function renderNav(active: ActiveRoute = "landing"): HTMLElement {
  const links = NAV_LINKS[active].map((link) => {
    const cls = link.cta ? "nav-cta" : undefined;
    const attrs: Record<string, string> = { href: link.href };
    if (cls) attrs.class = cls;
    if (link.external) {
      attrs.target = "_blank";
      attrs.rel = "noopener";
    }
    return h("a", attrs, link.label);
  });

  return h("nav", { class: "site-nav" },
    h("div", { class: "nav-inner" }, [
      h("a", { href: "/", class: "brand" }, [
        h("span", { class: "brand-mark" }),
        "dawhub",
        h("span", { class: "beta-pill" }, "BETA"),
      ]),
      h("span", { class: "nav-tagline" }, NAV_TAGLINE[active]),
      h("div", { class: "nav-right" }, links),
    ]),
  );
}

export function renderFooter(): HTMLElement {
  const year = new Date().getFullYear();
  return h("footer", { class: "site-footer" },
    h("div", { class: "footer-inner" }, [
      h("span", null, `© ${year} DAWHUB // BUILT FOR PRODUCERS`),
      h("div", { class: "footer-links" }, [
        h("a", { href: "/flpdiff" }, "FLPDIFF"),
        h("a", { href: GITHUB_URL, target: "_blank", rel: "noopener" }, "GITHUB"),
        h("a", { href: "mailto:roman@pronskiy.com" }, "CONTACT"),
      ]),
    ]),
  );
}

/**
 * Just the email + submit pair. The surrounding section (heading,
 * background, copy) is rendered by callers — the hero and the final
 * CTA on the landing each style it differently.
 */
export function renderWaitlistForm(opts: { ariaLabelPrefix?: string } = {}): HTMLElement {
  const action = MAILCOACH_SUBSCRIBE_URL || "#";
  const prefix = opts.ariaLabelPrefix ?? "";
  return h("form", {
    class: "waitlist-form",
    action,
    method: "post",
    onsubmit: (ev: Event) => {
      if (!MAILCOACH_SUBSCRIBE_URL) ev.preventDefault();
    },
  }, [
    h("input", {
      type: "email",
      name: "email",
      placeholder: "you@yourdomain.com",
      required: true,
      autocomplete: "email",
      "aria-label": prefix ? `${prefix} email` : "Your email",
    }),
    h("button", { type: "submit", class: "btn" }, "JOIN →"),
  ]);
}
